import React from 'react';
import { GenericTag } from '@/styles/GenericTag';
import { Trash2, Copy, Download, Upload } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { useProject } from '@/components/ProjectSelector';
import { BillsService, saveBill } from '../services/billsService';
import { useSearchParams } from 'react-router-dom';
import { Bill } from '../types';
import { trackButtonClick } from '@/lib/posthog-events';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { BillForm } from './BillForm';
import { fetchEntityAttachments } from '@/lib/entity-attachments';

const formatBillId = (bill: Bill): string => bill.bill_id;

export function BillsTable() {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  // The list: ONE server-driven query (project + search + sort + page) -- search, sort and paging run in the database
  // over every bill the user may see; the counters and each bill's amount paid come with the row (hooks/useEntityList.ts).
  const list = useEntityList<any, Bill>({
    entityType: 'bill',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    includeRollups: true,
    defaultSort: { columnKey: 'id', direction: 'asc', sortKey: 'bill_id' },
    mapRow: (row) => row,
  });
  const bills = list.items;
  const { projects } = useCompanyLookups(!!companyId, { includeInactiveProjects: true });
  const [currentBill, setCurrentBill] = React.useState<Bill | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [showForm, setShowForm] = React.useState(false);

  const handleNewBill = async () => {
    if (!companyId || !user) return;
    try {
      setCurrentBill({
        id: '',
        company_id: companyId,
        project_id: '',
        number: '',
        status: 'open',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        total_amount: 0,
        notes: '',
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        name: '',
        type: 'Bill',
        description: '',
        tax_pct: 0,
        other_amount: 0
      } as any);
      setShowForm(true);
    } catch (e) {
      console.error('Error creating new bill:', e);
    }
  };

  const handleEditBill = async (bill: Pick<Bill, 'id'>) => {
    if (!companyId) return;
    try {
      const billData = await BillsService.getBill(bill.id);
      if (billData) {
        const attachmentsWithSignedUrls = await fetchEntityAttachments('bill', bill.id);

        setCurrentBill({ 
          ...(billData as any).bill, 
          items: (billData as any).items,
          attachments: attachmentsWithSignedUrls
        } as any);
        setShowForm(true);
      }
    } catch (e) {
      console.error('Error loading bill:', e);
    }
  };


  // A link to one bill (/cost-management/bills?bill=<id>, e.g. the one "Create Bill" from a purchase order points at) opens it.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedBillId = searchParams.get('bill');
  React.useEffect(() => {
    if (!linkedBillId || !companyId) return;
    void handleEditBill({ id: linkedBillId });
    setSearchParams((prev) => { prev.delete('bill'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedBillId, companyId]);

  // Creates or updates the bill itself (ONE request). Its lines and files are saved by the form, in parallel, and the form
  // asks for the refresh (`onSaved`) when everything is done.
  const handleSaveBill = async (billData: any) => {
    if (!companyId || !user) return;
    return saveBill(billData, { companyId: companyId as string, userId: user.id });
  };

  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one bill to copy.');
      return;
    }
    if (!companyId || !user?.id) {
      alert('Please log in again to copy bills.');
      return;
    }

    let successCount = 0;
    let errorCount = 0;
    for (const bill of bills.filter((b) => selectedRows.includes(b.id))) {
      try {
        const source = await BillsService.getBill(bill.id);
        const original: any = source?.bill ?? bill;
        const copy = await BillsService.createBill({
          project_id: original.project_id || null,
          vendor_id: original.vendor_id || null,
          number: original.number || null,
          name: original.name || null,
          type: original.type || null,
          issue_date: original.issue_date,
          due_date: original.due_date,
          status: 'open',
          total_amount: original.total_amount || 0,
          notes: original.notes || null,
          description: original.description || null,
          tax_pct: original.tax_pct || null,
          other_amount: original.other_amount || null,
        });
        // A copy is a bill of its own: it keeps what each line says (item, cost code, unit, catalog item, amounts) but NOT the purchase-order line it
        // covered -- the copy is not linked to any order, so it must not count as billing that order's line a second time.
        const lines = (source?.items ?? []).map((item: any, index: number) => ({
          description: item.description, quantity: item.quantity, unit_price: item.unit_price, total_price: item.total_price, order_number: index + 1,
          cost_code_id: item.cost_code_id ?? null, catalog_item_id: item.catalog_item_id ?? null, unit: item.unit ?? null,
        }));
        if (lines.length > 0) await BillsService.replaceBillLines(copy.id, lines);
        successCount++;
      } catch (error) {
        console.error('Error copying bill:', error);
        errorCount++;
      }
    }

    list.reload();
    setSelectedRows([]);
    alert(errorCount > 0 ? `Copied ${successCount} bill(s) successfully. ${errorCount} failed.` : `Successfully copied ${successCount} bill(s).`);
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one bill to delete.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} bill(s)? This action cannot be undone.`)) {
      return;
    }
    if (!companyId || !user?.id) {
      alert('Please log in again to delete bills.');
      return;
    }

    // the server removes a bill's lines and files with it, and refuses a bill that still has payments (and says so)
    const results = await Promise.allSettled(selectedRows.map((billId) => BillsService.deleteBill(billId)));
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    list.reload();
    setSelectedRows([]);
    if (failures.length > 0) {
      const reasons = Array.from(new Set(failures.map((f) => (f.reason instanceof Error ? f.reason.message : 'Failed to delete'))));
      alert(`Deleted ${results.length - failures.length} bill(s). ${failures.length} could not be deleted:\n${reasons.join('\n')}`);
    } else {
      alert(`Successfully deleted ${results.length} bill(s).`);
    }
  };

  // Export to CSV: the bills selected in the table, or -- with nothing selected -- EVERY bill of the current project +
  // search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'bills', 'bills');

  // Map bill statuses to GenericTag status types
  const mapBillStatusToTag = (status: string): 'draft' | 'open' | 'paid' | 'partially_paid' | 'void' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft'; // Gray for draft (created, not yet sent)
      case 'paid':
        return 'paid'; // Green for paid
      case 'partially_paid':
        return 'partially_paid'; // Yellow/orange for partially paid
      case 'void':
        return 'void'; // Purple for void
      case 'open':
      default:
        return 'open'; // Red for open (default)
    }
  };

  // Format status text with proper capitalization
  const formatBillStatusText = (status: string): string => {
    return status.replace('_', ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase());
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<Bill>[] = [
    {
      key: 'id',
      header: 'Bill ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'bill_id',
      render: (row) => <span className="font-medium">{formatBillId(row)}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      width: '11.25rem', // 180px
      sortable: true,
      sortKey: 'name',
      render: (row) => row.name || 'Untitled Bill',
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '11.25rem', // 180px
      sortable: true,
      sortKey: 'project',
      render: (row) => (row as any).project?.name || projects.find(p => p.id === row.project_id)?.name || 'Unknown Project',
    },
    {
      key: 'vendor_id',
      header: 'Vendor',
      width: '11.25rem', // 180px
      sortable: true,
      sortKey: 'vendor',
      render: (row) => {
        const vendor = (row as any).vendor;
        return vendor ? (vendor.company_name || `${vendor.first_name || ''} ${vendor.last_name || ''}`.trim()) : 'No Vendor';
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '8.125rem', // 130px
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapBillStatusToTag(row.status)}
        >
          {formatBillStatusText(row.status)}
        </GenericTag>
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      width: '7rem',
      sortable: true,
      sortKey: 'due_date',
      render: (row) => {
        if (!row.due_date) return '-';
        const date = new Date(row.due_date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
    {
      key: 'amount_paid',
      header: 'Amount Paid',
      width: '8.75rem', // 140px
      sortable: true,
      sortKey: 'amount_paid',
      align: 'right' as const,
      render: (row) => `$${Number((row as any).amount_paid || 0).toFixed(2)}`,
    },
    {
      key: 'balance_due',
      header: 'Balance Due',
      width: '8.75rem', // 140px
      sortable: true,
      sortKey: 'balance_due',
      align: 'right' as const,
      render: (row) => `$${Math.max(0, Number(row.total_amount || 0) - Number((row as any).amount_paid || 0)).toFixed(2)}`,
    },
    {
      key: 'total_amount',
      header: 'Amount',
      width: '8.125rem', // 130px
      align: 'right' as const,
      sortable: true,
      sortKey: 'total_amount',
      render: (row) => `$${Number(row.total_amount || 0).toFixed(2)}`,
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => <span className="text-center block">{(row as any).attachment_count || 0}</span>,
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'comment_count',
      render: (row) => <span className="text-center block">{(row as any).comment_count || 0}</span>,
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <NewItemButton
            label="New"
            onClick={() => {
              trackButtonClick('new_bill_button', { context: 'cost_management' });
              handleNewBill();
            }}
          />
          <SearchButton
            onSearch={list.setSearchInput}
            placeholder="Search"
          />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      {/* Generic Table */}
      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={1200}
        onRowClick={handleEditBill}
        selectable={true}
        selectedKeys={selectedRows}
        onSelectionChange={setSelectedRows}
      />

      <BillForm
        bill={currentBill}
        projects={projects}
        onClose={() => {
          setShowForm(false);
          setCurrentBill(null);
        }}
        onSave={handleSaveBill}
        onSaved={list.reload}
        isOpen={showForm}
      />
    </div>
  );
}

export default BillsTable;

